// client/src/pages/MemberProfile.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react";
import { isPlayStoreApp } from "../lib/appMode";
import { resolveBadgeImage } from "../lib/badges";
import PushNotificationSettings from "../components/PushNotificationSettings";
import PublicProfileCustomizer from "../components/PublicProfileCustomizer";
import { unregisterCurrentPushDevice } from "../lib/nativePush";

function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

function getUser() {
  const raw = localStorage.getItem("user");
  const parsed = raw ? safeJsonParse(raw) : null;
  return parsed && typeof parsed === "object" ? parsed : null;
}

async function clearAuth() {
  const token = getToken();
  await unregisterCurrentPushDevice(token).catch(() => {});
  localStorage.removeItem("ga_token");
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  window.dispatchEvent(new Event("auth:changed"));
}

function toAbsUrl(apiBase, maybeRel) {
  if (!maybeRel) return "";
  const s = String(maybeRel).trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (!apiBase) return s;
  return `${apiBase}${s.startsWith("/") ? "" : "/"}${s}`;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

const MAX_GAMEPLAY_UPLOAD_SECONDS = 150;
const AVATAR_CROP_PREVIEW_SIZE = 250;
const AVATAR_CROP_OUTPUT_SIZE = 512;

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, Number(value || 0)));
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds || 0)));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const cleanup = () => {
      video.removeAttribute("src");
      try {
        video.load();
      } catch {}
      URL.revokeObjectURL(objectUrl);
    };

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const duration = Number(video.duration || 0);
      if (!Number.isFinite(duration) || duration <= 0) {
        finish(reject, new Error("video_duration_unavailable"));
        return;
      }
      finish(resolve, duration);
    };
    video.onerror = () => finish(reject, new Error("video_metadata_failed"));
    video.src = objectUrl;
  });
}

async function squareCropImageFile(file, outputSize = 512) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("badge_image_unreadable"));
      img.src = objectUrl;
    });

    const sourceSize = Math.min(
      Number(image.naturalWidth || image.width || 0),
      Number(image.naturalHeight || image.height || 0)
    );
    if (!sourceSize) throw new Error("badge_image_unreadable");

    const sourceX = Math.max(0, (Number(image.naturalWidth || image.width) - sourceSize) / 2);
    const sourceY = Math.max(0, (Number(image.naturalHeight || image.height) - sourceSize) / 2);
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("badge_crop_unavailable");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error("badge_crop_failed"))),
        "image/png"
      );
    });
    return new File([blob], "custom-badge.png", { type: "image/png" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function MemberProfile() {
  const nav = useNavigate();
  const playStoreApp = isPlayStoreApp();
  const avatarFileRef = useRef(null);
  const uploadFileRef = useRef(null);
  const customBadgeFileRef = useRef(null);
  const wakeLockRef = useRef(null);
  const avatarCropImageRef = useRef(null);
  const avatarCropObjectUrlRef = useRef("");
  const avatarCropDragRef = useRef(null);

  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, ""); // prevent .../api/api
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const [token, setToken] = useState(() => getToken());
  const [user, setUser] = useState(() => getUser());
  const [avatarImgOk, setAvatarImgOk] = useState(true);

  // auto-refresh state
  const [bootLoading, setBootLoading] = useState(false);

  // modal + forms
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState("");

  // Facebook-style avatar positioning before upload. The final saved file is
  // cropped to a square so the framing stays consistent on the member profile.
  const [avatarCropOpen, setAvatarCropOpen] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState("");
  const [avatarCropName, setAvatarCropName] = useState("profile-photo");
  const [avatarCropNatural, setAvatarCropNatural] = useState({ width: 0, height: 0 });
  const [avatarCropZoom, setAvatarCropZoom] = useState(1);
  const [avatarCropOffset, setAvatarCropOffset] = useState({ x: 0, y: 0 });
  const [avatarCropDragging, setAvatarCropDragging] = useState(false);
  const [avatarCropSaving, setAvatarCropSaving] = useState(false);
  const [avatarCropErr, setAvatarCropErr] = useState("");

  const [formUsername, setFormUsername] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formBio, setFormBio] = useState("");
  const [socialTwitch, setSocialTwitch] = useState("");
  const [socialYouTube, setSocialYouTube] = useState("");
  const [socialTikTok, setSocialTikTok] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialTwitter, setSocialTwitter] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");

  // uploads
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStep, setUploadStep] = useState(""); // human text
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsErr, setUploadsErr] = useState("");
  const [flashMsg, setFlashMsg] = useState("");
  const [deletingId, setDeletingId] = useState(""); // NEW

  // notifications
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifActionBusy, setNotifActionBusy] = useState(false);
  const [membershipActive, setMembershipActive] = useState(false);
  const [membershipChecking, setMembershipChecking] = useState(false);
  const [notifErr, setNotifErr] = useState("");
  const [tournamentAchievements, setTournamentAchievements] = useState([]);
  const [tournamentAchievementStats, setTournamentAchievementStats] = useState({ championshipCount: 0, totalTournamentEarningsCents: 0 });
  const [tournamentAchievementsLoading, setTournamentAchievementsLoading] = useState(false);
  const [tournamentAchievementsErr, setTournamentAchievementsErr] = useState("");
  const [badgeCatalog, setBadgeCatalog] = useState([]);
  const [badgesOpen, setBadgesOpen] = useState(false);
  const [badgeBusy, setBadgeBusy] = useState("");
  const [badgeErr, setBadgeErr] = useState("");
  const [customBadgeBusy, setCustomBadgeBusy] = useState(false);

  function persistUser(nextUser) {
    const serialized = JSON.stringify(nextUser);
    localStorage.setItem("user", serialized);
    localStorage.setItem("ga_user", serialized);
    window.dispatchEvent(new Event("auth:changed"));
  }

  // Keep local state aligned if auth changes elsewhere
  useEffect(() => {
    const refresh = () => {
      setToken(getToken());
      setUser(getUser());
    };
    refresh();
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const equippedBadge = useMemo(() => {
    const key = String(user?.equippedBadgeKey || "");
    return badgeCatalog.find((badge) => badge.key === key && badge.unlocked) || null;
  }, [badgeCatalog, user?.equippedBadgeKey]);

  const equippedBadgeImage = useMemo(() => {
    if (!equippedBadge) return "";
    return resolveBadgeImage(
      equippedBadge.key,
      equippedBadge.key === "custom_badge" ? equippedBadge.image || user?.customBadgeUrl : "",
      API_BASE
    );
  }, [API_BASE, equippedBadge, user?.customBadgeUrl]);

  function getBadgeImage(badge) {
    if (!badge) return "";
    return resolveBadgeImage(
      badge.key,
      badge.key === "custom_badge" ? badge.image || user?.customBadgeUrl : "",
      API_BASE
    );
  }

  async function loadBadges(openAfter = false) {
    const t = getToken();
    if (!t) return;
    setBadgeErr("");
    try {
      const response = await fetch(`${API_BASE}/api/profile/badges`, { headers: { Authorization: `Bearer ${t}` } });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) throw new Error(data?.message || "Could not load badges.");
      setBadgeCatalog(Array.isArray(data.badges) ? data.badges : []);
      if (openAfter) setBadgesOpen(true);
    } catch (error) { setBadgeErr(error?.message || "Could not load badges."); }
  }

  useEffect(() => {
    if (!token) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("badges") === "1") loadBadges(true);
    } catch {}
    // The URL trigger only needs to run when the signed-in session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function equipBadge(key) {
    const t = getToken();
    if (!t) return;
    setBadgeBusy(key || "remove"); setBadgeErr("");
    try {
      const response = await fetch(`${API_BASE}/api/profile/badges/equipped`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ badgeKey: key }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) throw new Error(data?.message || "Could not equip badge.");
      const merged = { ...(getUser() || {}), ...(data.user || {}) };
      persistUser(merged); setUser(merged); setBadgeCatalog(Array.isArray(data.badges) ? data.badges : []);
    } catch (error) { setBadgeErr(error?.message || "Could not equip badge."); }
    finally { setBadgeBusy(""); }
  }


  async function handleCustomBadgeFile(file) {
    if (!file || customBadgeBusy) return;
    const type = String(file.type || "").toLowerCase();
    if (!type.startsWith("image/")) {
      setBadgeErr("Choose a PNG, JPG, or WebP image for your custom badge.");
      return;
    }
    if (Number(file.size || 0) > 3 * 1024 * 1024) {
      setBadgeErr("Custom badge images must be 3 MB or smaller.");
      return;
    }

    setCustomBadgeBusy(true);
    setBadgeErr("");
    try {
      const croppedFile = await squareCropImageFile(file, 512);
      const formData = new FormData();
      formData.append("badge", croppedFile);
      const response = await fetch(`${API_BASE}/api/profile/badges/custom`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Could not save the custom badge.");
      }
      const merged = { ...(getUser() || {}), ...(data.user || {}) };
      persistUser(merged);
      setUser(merged);
      setBadgeCatalog(Array.isArray(data.badges) ? data.badges : []);
      setFlashMsg("Custom badge saved. Open My Badges to equip it.");
      setTimeout(() => setFlashMsg(""), 4000);
    } catch (error) {
      const code = String(error?.message || "");
      setBadgeErr(
        code === "badge_image_unreadable"
          ? "That image could not be opened. Try a standard PNG, JPG, or WebP file."
          : code || "Could not save the custom badge."
      );
    } finally {
      setCustomBadgeBusy(false);
    }
  }

  const isSignedIn = !!token && !!user;

  const userId = useMemo(() => String(user?.id || ""), [user]);

  const displayName = useMemo(() => {
    const u = user || {};
    return u.displayName || u.username || u.name || "Member";
  }, [user]);

  const email = useMemo(() => {
    const u = user || {};
    return u.email || "";
  }, [user]);

  const bio = useMemo(() => {
    const u = user || {};
    return u.bio || "";
  }, [user]);

  const socials = useMemo(() => {
    const u = user || {};
    return u.socials && typeof u.socials === "object" ? u.socials : {};
  }, [user]);

  const wagerStats = useMemo(() => {
    const ws = user?.wagerStats && typeof user.wagerStats === "object" ? user.wagerStats : {};
    return {
      wins: Number(ws.wins || 0),
      losses: Number(ws.losses || 0),
      completed: Number(ws.completed || 0),
      disputed: Number(ws.disputed || 0),
    };
  }, [user]);

  const avatarSrc = useMemo(() => {
    const u = user || {};
    return toAbsUrl(API_BASE, u.avatarUrl);
  }, [API_BASE, user]);

  useEffect(() => setAvatarImgOk(true), [avatarSrc]);

  const avatarLetter = (displayName || "T")[0]?.toUpperCase();

  const avatarCropGeometry = useMemo(() => {
    const naturalWidth = Math.max(1, Number(avatarCropNatural.width || 1));
    const naturalHeight = Math.max(1, Number(avatarCropNatural.height || 1));
    const fitScale = Math.max(
      AVATAR_CROP_PREVIEW_SIZE / naturalWidth,
      AVATAR_CROP_PREVIEW_SIZE / naturalHeight
    );
    const scale = fitScale * clampNumber(avatarCropZoom, 1, 3);
    const displayWidth = naturalWidth * scale;
    const displayHeight = naturalHeight * scale;

    return {
      naturalWidth,
      naturalHeight,
      fitScale,
      scale,
      displayWidth,
      displayHeight,
      maxX: Math.max(0, (displayWidth - AVATAR_CROP_PREVIEW_SIZE) / 2),
      maxY: Math.max(0, (displayHeight - AVATAR_CROP_PREVIEW_SIZE) / 2),
    };
  }, [avatarCropNatural, avatarCropZoom]);

  useEffect(() => {
    setAvatarCropOffset((current) => ({
      x: clampNumber(current.x, -avatarCropGeometry.maxX, avatarCropGeometry.maxX),
      y: clampNumber(current.y, -avatarCropGeometry.maxY, avatarCropGeometry.maxY),
    }));
  }, [avatarCropGeometry.maxX, avatarCropGeometry.maxY]);

  useEffect(() => {
    return () => {
      if (avatarCropObjectUrlRef.current) {
        URL.revokeObjectURL(avatarCropObjectUrlRef.current);
        avatarCropObjectUrlRef.current = "";
      }
    };
  }, []);

  // ✅ AUTO-REFRESH PROFILE FROM BACKEND ON LOAD
  useEffect(() => {
    let cancelled = false;

    async function hydrateFromApi() {
      const t = getToken();
      if (!t) return;

      setBootLoading(true);

      try {
        const r = await fetch(`${API_BASE}/api/profile/me`, {
          headers: { Authorization: `Bearer ${t}` },
        });

        const data = await r.json().catch(() => null);
        if (!r.ok || !data?.ok) return;

        const nextUser = data.user;
        if (!cancelled && Array.isArray(data.badgeCatalog)) setBadgeCatalog(data.badgeCatalog);
        if (nextUser && typeof nextUser === "object") {
          const current = getUser() || {};
          const merged = { ...current, ...nextUser };
          persistUser(merged);
          if (!cancelled) setUser(merged);
        }
      } catch {
        // intentionally silent
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    }

    hydrateFromApi();
    return () => {
      cancelled = true;
    };
  }, [API_BASE]);

  // Load uploads list (posts) for this user
  async function loadUploads() {
    if (!userId) return;
    setUploadsLoading(true);
    setUploadsErr("");
    try {
      const r = await fetch(`${API_BASE}/api/posts/user/${encodeURIComponent(userId)}`);
      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.ok) throw new Error(data?.error || "Failed to load uploads");
      setUploads(Array.isArray(data.posts) ? data.posts : []);
    } catch (e) {
      setUploadsErr(e?.message || "Failed to load uploads");
    } finally {
      setUploadsLoading(false);
    }
  }



async function checkMembershipStatus() {
  const targetEmail = String(email || localStorage.getItem("member_email") || "").trim().toLowerCase();
  if (!targetEmail) {
    setMembershipActive(false);
    return;
  }

  try {
    setMembershipChecking(true);
    const r = await fetch(`${API_BASE}/api/subscriptions/me?email=${encodeURIComponent(targetEmail)}`);
    const data = await r.json().catch(() => ({}));
    const active = !!(data?.member?.active || data?.member === true);
    setMembershipActive(active);
    try {
      localStorage.setItem("member_email", targetEmail);
      localStorage.setItem("member_status", active ? "active" : "inactive");
    } catch {}
  } catch {
    setMembershipActive(false);
  } finally {
    setMembershipChecking(false);
  }
}

async function loadTournamentAchievements() {
  if (!token) return;
  setTournamentAchievementsLoading(true);
  setTournamentAchievementsErr("");
  try {
    const r = await fetch(`${API_BASE}/api/tournaments/profile/achievements`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => null);
    if (!r.ok || !data?.ok) throw new Error(data?.message || data?.error || "Failed to load tournament achievements");
    setTournamentAchievements(Array.isArray(data.achievements) ? data.achievements : []);
    setTournamentAchievementStats({
      championshipCount: Number(data.championshipCount || 0),
      totalTournamentEarningsCents: Number(data.totalTournamentEarningsCents || 0),
    });
  } catch (e) {
    setTournamentAchievementsErr(e?.message || "Failed to load tournament achievements");
    setTournamentAchievements([]);
  } finally {
    setTournamentAchievementsLoading(false);
  }
}

// Load notifications for this user (auth required)
async function loadNotifications() {
  if (!token) return;
  setNotifLoading(true);
  setNotifErr("");
  try {
    const r = await fetch(`${API_BASE}/api/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => null);
    if (r.status === 401) {
      setNotifErr("Session expired. Please sign in again.");
      setNotifications([]);
      return;
    }
    if (!r.ok || !data?.ok) throw new Error(data?.error || "Failed to load notifications");
    setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
  } catch (e) {
    setNotifErr(e?.message || "Failed to load notifications");
    setNotifications([]);
  } finally {
    setNotifLoading(false);
  }
}

async function markAllNotificationsRead() {
  if (!token) return;
  try {
    await fetch(`${API_BASE}/api/notifications/read-all`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    await loadNotifications();
  } catch {
    // ignore
  }
}

async function markNotificationRead(id) {
  if (!token || !id) return;
  try {
    await fetch(`${API_BASE}/api/notifications/mark-read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    await loadNotifications();
  } catch {
    // ignore
  }
}

async function deleteReadNotification(id) {
  if (!token || !id || notifActionBusy) return;
  setNotifActionBusy(true);
  setNotifErr("");
  try {
    const r = await fetch(`${API_BASE}/api/notifications/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      throw new Error(data?.message || data?.error || "Could not remove notification");
    }
    setNotifications((current) => current.filter((notification) => notification?.id !== id));
  } catch (e) {
    setNotifErr(e?.message || "Could not remove notification.");
  } finally {
    setNotifActionBusy(false);
  }
}

async function clearReadNotifications() {
  if (!token || notifActionBusy) return;
  const hasRead = notifications.some((notification) => Boolean(notification?.readAt || notification?.read));
  if (!hasRead) return;
  if (!window.confirm("Remove all notifications that have already been read?")) return;

  setNotifActionBusy(true);
  setNotifErr("");
  try {
    const r = await fetch(`${API_BASE}/api/notifications/read`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      throw new Error(data?.message || data?.error || "Could not clear read notifications");
    }
    setNotifications((current) =>
      current.filter((notification) => !(notification?.readAt || notification?.read))
    );
  } catch (e) {
    setNotifErr(e?.message || "Could not clear read notifications.");
  } finally {
    setNotifActionBusy(false);
  }
}

  useEffect(() => {
    if (isSignedIn && userId) loadUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, userId]);

  useEffect(() => {
    if (isSignedIn) checkMembershipStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, email]);


  useEffect(() => {
    if (!isSignedIn || !token) return;
    loadNotifications();
    const id = setInterval(loadNotifications, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, token]);

  useEffect(() => {
    if (!isSignedIn || !token) return;
    loadTournamentAchievements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, token]);

  useEffect(() => {
    if (!uploading) return undefined;

    const warnBeforeClose = (event) => {
      event.preventDefault();
      event.returnValue = "A gameplay upload is still in progress.";
      return event.returnValue;
    };
    const reacquireWakeLock = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        requestUploadWakeLock();
      }
    };

    window.addEventListener("beforeunload", warnBeforeClose);
    document.addEventListener("visibilitychange", reacquireWakeLock);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeClose);
      document.removeEventListener("visibilitychange", reacquireWakeLock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  // --- Styles ---
  const glassCard = {
    maxWidth: 980,
    width: "min(980px, calc(100% - 40px))",
    margin: "120px auto 40px",
    borderRadius: 22,
    padding: 26,
    background:
      "radial-gradient(1200px 600px at 20% 10%, rgba(255, 0, 64, 0.18), transparent 60%), radial-gradient(900px 500px at 90% 30%, rgba(255, 70, 70, 0.10), transparent 55%), rgba(8, 10, 16, 0.62)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    color: "rgba(255,255,255,0.92)",
  };

  const gridTop = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 360px)",
    gap: 26,
    alignItems: "start",
  };

  const profileIdentityCluster = {
    display: "flex",
    alignItems: "flex-start",
    gap: 22,
    minWidth: 0,
    textAlign: "left",
  };

  const avatarCircle = {
    width: 112,
    height: 112,
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255, 0, 64, 0.18)",
    display: "grid",
    placeItems: "center",
    fontSize: 34,
    fontWeight: 900,
    userSelect: "none",
    overflow: "visible",
    position: "relative",
    cursor: isSignedIn ? "pointer" : "default",
  };

  const avatarImg = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "50% 50%",
    display: "block",
  };

  const h1 = { fontSize: 40, fontWeight: 900, letterSpacing: 0.2 };
  const sub = { fontSize: 14, opacity: 0.85 };
  const small = { fontSize: 13, opacity: 0.8 };

  const pill = {
    borderRadius: 999,
    padding: "10px 16px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.35)",
    color: "rgba(255,255,255,0.92)",
    fontWeight: 800,
    cursor: "pointer",
  };

  const pillGreen = {
    ...pill,
    border: "1px solid rgba(255, 0, 64, 0.32)",
    background: "linear-gradient(90deg, rgba(255,0,64,0.85), rgba(190,0,38,0.70))",
  };

  const pillRed = {
    ...pill,
    border: "1px solid rgba(255,90,90,0.35)",
    background: "rgba(120,0,0,0.28)",
  };

  const pillDark = { ...pill, background: "rgba(0,0,0,0.45)" };

  const sectionTitle = { fontSize: 16, fontWeight: 900, opacity: 0.95, marginBottom: 10 };
  const sectionText = { margin: 0, opacity: 0.88, lineHeight: 1.5, fontSize: 14 };

  const linkList = { display: "grid", gap: 10 };
  const linkRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.25)",
  };

  const uploadCard = {
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.25)",
    display: "grid",
    gap: 8,
  };

  const videoWrap = {
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.35)",
  };

  function openEdit() {
    const u = user || {};
    const s = (u.socials && typeof u.socials === "object" ? u.socials : {}) || {};
    setFormUsername(u.username || u.name || "");
    setFormDisplayName(u.displayName || u.username || u.name || "");
    setFormBio(u.bio || "");
    setSocialTwitch(s.twitch || "");
    setSocialYouTube(s.youtube || "");
    setSocialTikTok(s.tiktok || "");
    setSocialInstagram(s.instagram || "");
    setSocialTwitter(s.twitter || "");
    setSocialWebsite(s.website || "");
    setEditErr("");
    setEditOpen(true);
  }

  async function saveProfile() {
    if (!token) return;
    setSaving(true);
    setEditErr("");
    try {
      const payload = {
        username: formUsername,
        displayName: formDisplayName,
        bio: formBio,
        socials: {
          twitch: socialTwitch,
          youtube: socialYouTube,
          tiktok: socialTikTok,
          instagram: socialInstagram,
          twitter: socialTwitter,
          website: socialWebsite,
        },
      };

      const res = await fetch(`${API_BASE}/api/profile/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Failed to save profile");
      }

      if (data?.token) {
        localStorage.setItem("ga_token", data.token);
        localStorage.setItem("token", data.token);
        localStorage.setItem("authToken", data.token);
        setToken(data.token);
      }

      const nextUser = { ...(user || {}), ...(data.user || {}) };
      persistUser(nextUser);
      setUser(nextUser);

      if (data?.usernameChanged) {
        setFlashMsg(
          `Username changed to @${nextUser.username}. Your previous username is now available.`
        );
        setTimeout(() => setFlashMsg(""), 5000);
      }

      setEditOpen(false);
    } catch (e) {
      setEditErr(e?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  async function uploadAvatar(file) {
    if (!token || !file) return { ok: false, error: "missing_file" };
    setSaving(true);
    setEditErr("");
    try {
      const fd = new FormData();
      fd.append("avatar", file);

      const res = await fetch(`${API_BASE}/api/profile/avatar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to upload avatar");

      const nextUser = { ...(user || {}), ...(data.user || {}), avatarUrl: data.avatarUrl };
      persistUser(nextUser);
      setUser(nextUser);
      return { ok: true, user: nextUser };
    } catch (e) {
      const message = e?.message || "Failed to upload avatar";
      setEditErr(message);
      return { ok: false, error: message };
    } finally {
      setSaving(false);
    }
  }

  function releaseAvatarCropObjectUrl() {
    if (!avatarCropObjectUrlRef.current) return;
    URL.revokeObjectURL(avatarCropObjectUrlRef.current);
    avatarCropObjectUrlRef.current = "";
  }

  function closeAvatarCrop(force = false) {
    if (avatarCropSaving && !force) return;
    releaseAvatarCropObjectUrl();
    avatarCropImageRef.current = null;
    avatarCropDragRef.current = null;
    setAvatarCropOpen(false);
    setAvatarCropSrc("");
    setAvatarCropNatural({ width: 0, height: 0 });
    setAvatarCropZoom(1);
    setAvatarCropOffset({ x: 0, y: 0 });
    setAvatarCropDragging(false);
    setAvatarCropErr("");
  }

  function openAvatarCropSource(sourceUrl, fileName = "profile-photo") {
    if (!sourceUrl) return;

    releaseAvatarCropObjectUrl();
    avatarCropObjectUrlRef.current = sourceUrl;
    setAvatarCropErr("");
    setAvatarCropName(fileName || "profile-photo");
    setAvatarCropZoom(1);
    setAvatarCropOffset({ x: 0, y: 0 });

    const image = new Image();
    image.onload = () => {
      avatarCropImageRef.current = image;
      setAvatarCropNatural({
        width: Math.max(1, Number(image.naturalWidth || image.width || 1)),
        height: Math.max(1, Number(image.naturalHeight || image.height || 1)),
      });
      setAvatarCropSrc(sourceUrl);
      setAvatarCropOpen(true);
    };
    image.onerror = () => {
      releaseAvatarCropObjectUrl();
      setAvatarCropErr("That picture could not be opened. Try another image.");
    };
    image.src = sourceUrl;
  }

  function prepareAvatarFile(file) {
    if (!file) return;
    const type = String(file.type || "").toLowerCase();
    if (!type.startsWith("image/")) {
      setEditErr("Choose a PNG, JPG, or WebP image.");
      return;
    }
    if (Number(file.size || 0) > 5 * 1024 * 1024) {
      setEditErr("Profile pictures must be 5 MB or smaller.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    openAvatarCropSource(objectUrl, file.name || "profile-photo");
  }

  async function repositionExistingAvatar() {
    if (!avatarSrc) return;
    setEditErr("");
    try {
      const response = await fetch(avatarSrc, { cache: "no-store" });
      if (!response.ok) throw new Error("photo_fetch_failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      openAvatarCropSource(objectUrl, "profile-photo");
    } catch (_error) {
      setEditErr("That saved photo could not be reopened. Choose the picture again to reposition it.");
      if (avatarFileRef.current) avatarFileRef.current.click();
    }
  }

  function onPickAvatar() {
    if (!isSignedIn) return;
    const rawAvatarUrl = String(user?.avatarUrl || "");
    if (rawAvatarUrl.includes("/uploads/avatars/") && avatarSrc && avatarImgOk) {
      repositionExistingAvatar();
      return;
    }
    if (avatarFileRef.current) avatarFileRef.current.click();
  }

  function startAvatarCropDrag(event) {
    if (avatarCropSaving) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    avatarCropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: avatarCropOffset.x,
      offsetY: avatarCropOffset.y,
    };
    setAvatarCropDragging(true);
  }

  function moveAvatarCropDrag(event) {
    const drag = avatarCropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setAvatarCropOffset({
      x: clampNumber(
        drag.offsetX + (event.clientX - drag.startX),
        -avatarCropGeometry.maxX,
        avatarCropGeometry.maxX
      ),
      y: clampNumber(
        drag.offsetY + (event.clientY - drag.startY),
        -avatarCropGeometry.maxY,
        avatarCropGeometry.maxY
      ),
    });
  }

  function finishAvatarCropDrag(event) {
    const drag = avatarCropDragRef.current;
    if (!drag || (event?.pointerId !== undefined && drag.pointerId !== event.pointerId)) return;
    try {
      event?.currentTarget?.releasePointerCapture?.(drag.pointerId);
    } catch {}
    avatarCropDragRef.current = null;
    setAvatarCropDragging(false);
  }

  function resetAvatarCrop() {
    setAvatarCropZoom(1);
    setAvatarCropOffset({ x: 0, y: 0 });
  }

  async function savePositionedAvatar() {
    const image = avatarCropImageRef.current;
    if (!image || !avatarCropNatural.width || !avatarCropNatural.height) {
      setAvatarCropErr("The picture is still loading. Try again in a moment.");
      return;
    }

    setAvatarCropSaving(true);
    setAvatarCropErr("");

    try {
      const { naturalWidth, naturalHeight, scale } = avatarCropGeometry;
      const sourceSize = AVATAR_CROP_PREVIEW_SIZE / scale;
      const sourceX = clampNumber(
        (naturalWidth - sourceSize) / 2 - avatarCropOffset.x / scale,
        0,
        Math.max(0, naturalWidth - sourceSize)
      );
      const sourceY = clampNumber(
        (naturalHeight - sourceSize) / 2 - avatarCropOffset.y / scale,
        0,
        Math.max(0, naturalHeight - sourceSize)
      );

      const canvas = document.createElement("canvas");
      canvas.width = AVATAR_CROP_OUTPUT_SIZE;
      canvas.height = AVATAR_CROP_OUTPUT_SIZE;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("crop_canvas_unavailable");

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        AVATAR_CROP_OUTPUT_SIZE,
        AVATAR_CROP_OUTPUT_SIZE
      );

      const croppedBlob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("crop_export_failed"))),
          "image/png"
        );
      });

      const safeBaseName = String(avatarCropName || "profile-photo")
        .replace(/\.[^.]+$/, "")
        .replace(/[^a-z0-9_-]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "profile-photo";
      const croppedFile = new File([croppedBlob], `${safeBaseName}-positioned.png`, {
        type: "image/png",
      });

      const result = await uploadAvatar(croppedFile);
      if (!result?.ok) throw new Error(result?.error || "Failed to upload avatar");

      closeAvatarCrop(true);
      setFlashMsg("Profile photo saved with your new position.");
      setTimeout(() => setFlashMsg(""), 4000);
    } catch (error) {
      setAvatarCropErr(error?.message || "Failed to save the positioned photo.");
    } finally {
      setAvatarCropSaving(false);
    }
  }

  function openUploader() {
    setUploadErr("");
    setUploadCaption("");
    setUploadProgress(0);
    setUploadStep("");
    setUploadOpen(true);
  }

  function pickUploadFile() {
    if (uploadFileRef.current) uploadFileRef.current.click();
  }

  async function createMuxUpload(durationSeconds) {
    const r = await fetch(`${API_BASE}/api/mux/uploads`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        cors_origin: window.location.origin,
        durationSeconds: Number(durationSeconds || 0),
      }),
    });
    const data = await r.json().catch(() => null);
    if (!r.ok || !data?.ok) throw new Error(data?.error || "mux_upload_create_failed");
    return data;
  }

  function waitForUploadConnection() {
    if (typeof navigator === "undefined" || navigator.onLine !== false) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const onOnline = () => {
        window.removeEventListener("online", onOnline);
        resolve();
      };
      window.addEventListener("online", onOnline, { once: true });
    });
  }

  function putMuxChunk(uploadUrl, chunk, file, startByte, totalBytes, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const endByte = startByte + chunk.size - 1;
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.setRequestHeader("Content-Range", `bytes ${startByte}-${endByte}/${totalBytes}`);
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        onProgress(startByte + event.loaded);
      };
      xhr.onload = () => {
        // Mux resumable uploads may return 308 until the final chunk.
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 308) {
          onProgress(startByte + chunk.size);
          resolve();
        } else {
          const error = new Error(`mux_upload_chunk_failed_${xhr.status}`);
          error.status = xhr.status;
          reject(error);
        }
      };
      xhr.onerror = () => reject(new Error("mux_upload_chunk_failed_network"));
      xhr.onabort = () => reject(new Error("mux_upload_chunk_aborted"));
      xhr.send(chunk);
    });
  }

  async function putToMux(uploadUrl, file, onProgress) {
    const totalBytes = Math.max(1, Number(file.size || 0));
    // Multiples of 256KB, as required by Mux resumable uploads.
    const chunkBytes = playStoreApp ? 8 * 1024 * 1024 : 16 * 1024 * 1024;
    const retryableStatuses = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

    for (let startByte = 0; startByte < totalBytes; startByte += chunkBytes) {
      const endByteExclusive = Math.min(totalBytes, startByte + chunkBytes);
      const chunk = file.slice(startByte, endByteExclusive);
      let attempt = 0;

      while (true) {
        await waitForUploadConnection();
        try {
          await putMuxChunk(uploadUrl, chunk, file, startByte, totalBytes, (uploadedBytes) => {
            onProgress(Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)));
          });
          break;
        } catch (error) {
          attempt += 1;
          const status = Number(error?.status || 0);
          const retryable =
            String(error?.message || "").includes("network") || retryableStatuses.has(status);
          if (!retryable || attempt >= 6) throw error;
          setUploadStep(`Connection interrupted — retrying chunk (${attempt}/5)…`);
          await sleep(Math.min(8000, 700 * 2 ** (attempt - 1)));
          setUploadStep("Uploading video in reliable chunks…");
        }
      }
    }
  }

  async function requestUploadWakeLock() {
    try {
      if (document.visibilityState === "visible" && navigator?.wakeLock?.request) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch {
      wakeLockRef.current = null;
    }
  }

  async function releaseUploadWakeLock() {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    try {
      await lock?.release?.();
    } catch {}
  }

  async function pollMux(uploadId) {
    for (let i = 0; i < 60; i++) {
      const r = await fetch(`${API_BASE}/api/mux/uploads/${encodeURIComponent(uploadId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => null);

      if (!r.ok || !data?.ok) {
        if (data?.error === "video_too_long") {
          const actual = Number(data?.durationSeconds || 0);
          throw new Error(
            actual > 0
              ? `That video is ${formatDuration(actual)}. Gameplay uploads must be 2:30 or shorter.`
              : "Gameplay uploads must be 2:30 or shorter."
          );
        }
        throw new Error(data?.error || "mux_upload_status_failed");
      }

      if (data.playbackId) {
        return {
          playbackId: data.playbackId,
          durationSeconds: Number(data?.durationSeconds || 0),
        };
      }
      if (data.status === "errored") throw new Error("mux_processing_errored");

      await sleep(2000);
    }
    throw new Error("mux_processing_timeout");
  }

  async function createPost(playbackId, caption, durationSeconds) {
    const r = await fetch(`${API_BASE}/api/posts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ playbackId, caption, durationSeconds }),
    });
    const data = await r.json().catch(() => null);
    if (!r.ok || !data?.ok) throw new Error(data?.error || "posts_create_failed");
    return data.post;
  }

  async function deletePost(postId) {
    if (!token || !postId) return;

    const ok = window.confirm("Delete this upload? This removes it from your profile AND the Feed.");
    if (!ok) return;

    setDeletingId(postId);
    setFlashMsg("");
    setUploadsErr("");

    try {
      // Optimistic remove
      setUploads((prev) => prev.filter((p) => String(p.id) !== String(postId)));

      const r = await fetch(`${API_BASE}/api/posts/${encodeURIComponent(postId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.ok) {
        // rollback (reload)
        await loadUploads();
        throw new Error(data?.error || "delete_failed");
      }

      setFlashMsg("Deleted ✅");
    } catch (e) {
      setUploadsErr(e?.message || "Failed to delete");
    } finally {
      setDeletingId("");
    }
  }

  async function handleUploadFile(file) {
    if (!file) return;
    if (!token) {
      setUploadErr("Please sign in again.");
      return;
    }

    if (!String(file.type || "").toLowerCase().startsWith("video/")) {
      setUploadErr("Please choose a video file.");
      return;
    }

    setUploading(true);
    setUploadErr("");
    setFlashMsg("");
    await requestUploadWakeLock();

    try {
      setUploadStep("Checking video length…");
      setUploadProgress(0);

      const localDurationSeconds = await readVideoDuration(file);
      if (localDurationSeconds > MAX_GAMEPLAY_UPLOAD_SECONDS + 0.25) {
        throw new Error(
          `That video is ${formatDuration(localDurationSeconds)}. Gameplay uploads must be 2:30 or shorter.`
        );
      }

      setUploadStep("Creating upload…");
      const { uploadId, uploadUrl } = await createMuxUpload(localDurationSeconds);

      setUploadStep("Uploading video in reliable chunks…");
      await putToMux(uploadUrl, file, (pct) => setUploadProgress(pct));

      setUploadStep("Processing…");
      setUploadProgress(100);

      const processed = await pollMux(uploadId);
      const playbackId = processed?.playbackId;
      const serverDurationSeconds =
        Number(processed?.durationSeconds || 0) || localDurationSeconds;

      setUploadStep("Publishing post…");
      await createPost(playbackId, uploadCaption, serverDurationSeconds);

      setUploadStep("Done!");
      setFlashMsg("Upload posted ✅");
      setUploadOpen(false);

      await loadUploads();
    } catch (e) {
      const code = String(e?.message || "");
      if (code === "video_duration_unavailable" || code === "video_metadata_failed") {
        setUploadErr("I could not read that video's length. Try a standard MP4, MOV, or WebM file.");
      } else {
        setUploadErr(code || "Upload failed");
      }
    } finally {
      await releaseUploadWakeLock();
      setUploading(false);
      setUploadStep("");
      setUploadProgress(0);
    }
  }

  if (!isSignedIn) {
    return (
      <div style={glassCard}>
        <div style={{ display: "grid", gap: 10 }}>
          <h2 style={{ margin: 0 }}>Sign in</h2>
          <p style={{ margin: 0, opacity: 0.85 }}>
            You’re signed out. Sign in to view and edit your member profile.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button style={pillGreen} onClick={() => nav("/signin")}>
              Go to sign in
            </button>
            <button style={pillDark} onClick={() => nav("/")}>
              Back home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={glassCard}>
      <input
        ref={avatarFileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) prepareAvatarFile(f);
        }}
      />

      <input
        ref={uploadFileRef}
        type="file"
        accept="video/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) handleUploadFile(f);
        }}
      />


      <input
        ref={customBadgeFileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) handleCustomBadgeFile(f);
        }}
      />

      {bootLoading ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            fontWeight: 800,
            fontSize: 13,
            opacity: 0.9,
          }}
        >
          Syncing your profile…
        </div>
      ) : null}

      {flashMsg ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 14,
            border: "1px solid rgba(255,0,64,0.22)",
            background: "rgba(255,0,64,0.10)",
            fontWeight: 900,
            fontSize: 13,
          }}
        >
          {flashMsg}
        </div>
      ) : null}

      <div style={{ ...gridTop }} className="_ga_profile_grid_fix">
        <div className="_ga_profile_identity_cluster" style={profileIdentityCluster}>
        <div
          style={avatarCircle}
          onClick={onPickAvatar}
          title={isSignedIn ? (String(user?.avatarUrl || "").includes("/uploads/avatars/") ? "Reposition profile photo" : "Upload profile photo") : ""}
        >
          <div style={{ width: "100%", height: "100%", borderRadius: 19, overflow: "hidden", display: "grid", placeItems: "center" }}>
            {avatarSrc && avatarImgOk ? (
              <img src={avatarSrc} alt="Profile" style={avatarImg} onError={() => setAvatarImgOk(false)} />
            ) : avatarLetter}
          </div>
          {equippedBadge && equippedBadgeImage ? (
            <img src={equippedBadgeImage} alt={equippedBadge.title} title={equippedBadge.title}
              style={{ position: "absolute", top: -12, left: -12, width: 44, height: 44, borderRadius: "50%", objectFit: "cover", zIndex: 3, border: "2px solid rgba(255,255,255,.78)", filter: "drop-shadow(0 4px 7px rgba(0,0,0,.85))" }} />
          ) : null}
        </div>

        <div>
          <div style={{ display: "grid", gap: 6 }}>
            <div style={h1}>{displayName}</div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                width: "fit-content",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(255,214,92,0.24)",
                background: "rgba(255,184,0,0.09)",
                color: "#ffd65c",
                fontSize: 12,
                fontWeight: 950,
              }}
            >
              ⛽ {Math.max(0, Number(user?.gasPoints || 0)).toLocaleString()} GAS Points
            </div>
            <div style={sub}>This is your Get Active profile.</div>
            {email ? (
              <div style={small}>
                Signed in as <strong>{email}</strong>
              </div>
            ) : null}

            <div style={{ ...small, marginTop: 2, opacity: 0.75 }}>
              Tip: click your profile picture to upload, drag, zoom, or reposition it.
            </div>

            <div style={{ marginTop: 10 }}><button type="button" style={pillDark} onClick={() => loadBadges(true)}>🏅 My Badges</button></div>

          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
            <button style={pillGreen} onClick={openUploader}>
              Upload gameplay
            </button>
            <button style={pillDark} onClick={openEdit}>
              Edit profile
            </button>
            <button
              style={pillDark}
              onClick={() => {
                const handle = String(user?.username || user?.name || "").trim();
                if (handle) nav(`/u/${encodeURIComponent(handle)}`);
              }}
            >
              View public profile
            </button>
            <button
              style={pillDark}
              onClick={async () => {
                await clearAuth();
                nav("/signin");
              }}
            >
              Sign out
            </button>
          </div>
        </div>
        </div>

        {!playStoreApp ? (
          <div
            style={{
              borderRadius: 18,
              padding: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background:
                "radial-gradient(900px 460px at 15% 10%, rgba(255,120,0,0.10), transparent 60%), rgba(8, 10, 16, 0.70)",
            }}
          >
            <div style={{ fontWeight: 800, opacity: 0.92 }}>Wager Record</div>
            <div style={{ marginTop: 8, fontSize: 28, fontWeight: 900, lineHeight: 1 }}>
              {wagerStats.wins}-{wagerStats.losses}
            </div>
            <div style={{ marginTop: 10, opacity: 0.82, fontSize: 14, lineHeight: 1.45 }}>
              Completed: <strong>{wagerStats.completed}</strong> · Disputed: <strong>{wagerStats.disputed}</strong>
            </div>
            <div style={{ marginTop: 8, opacity: 0.72, fontSize: 13 }}>
              Your member-vs-member wager performance updates after completed matches.
            </div>
          </div>
        ) : null}

        <div
          style={{
            borderRadius: 18,
            padding: 16,
            border: "1px solid rgba(255,255,255,0.12)",
            background:
              "radial-gradient(900px 460px at 15% 10%, rgba(255,0,64,0.14), transparent 60%), rgba(8, 10, 16, 0.70)",
          }}
        >
          <div style={{ fontWeight: 800, opacity: 0.92 }}>Membership</div>
          <div style={{ marginTop: 8, opacity: 0.85, lineHeight: 1.35, fontSize: 14 }}>
            {membershipChecking
              ? "Checking membership..."
              : membershipActive
              ? playStoreApp
                ? "Active paid member — ad-free stream viewing, tournament access, and member perks are unlocked."
                : "Active paid member — ad-free stream viewing, wagers, and tournament access are unlocked."
              : playStoreApp
                ? "Subscribe to unlock ad-free stream viewing, tournaments, giveaways, and more."
                : "Subscribe to unlock ad-free stream viewing, wagers, tournaments, giveaways, and more."}
          </div>
          <button
            style={{
              ...pillGreen,
              width: "100%",
              marginTop: 14,
              background: membershipActive
                ? "linear-gradient(90deg, rgba(0,160,255,0.90), rgba(110,70,255,0.78))"
                : "linear-gradient(90deg, rgba(255,0,64,0.90), rgba(190,0,38,0.78))",
            }}
            onClick={() => nav("/membership")}
          >
            {membershipActive ? "Manage / Cancel Membership" : "Get Active & Subscribe"}
          </button>
        </div>
      </div>


      <div
        id="tournament-achievements"
        style={{
          marginTop: 18,
          borderRadius: 18,
          padding: 16,
          border: "1px solid rgba(255,214,92,0.22)",
          background:
            "radial-gradient(900px 460px at 15% 10%, rgba(255,190,40,0.14), transparent 60%), rgba(8, 10, 16, 0.74)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 1000, fontSize: 18 }}>🏆 Tournament Achievements</div>
            <div style={{ marginTop: 4, opacity: 0.78, fontSize: 13 }}>
              Championships: <strong>{tournamentAchievementStats.championshipCount}</strong> · Tournament earnings: <strong>${(tournamentAchievementStats.totalTournamentEarningsCents / 100).toFixed(2)}</strong>
            </div>
          </div>
          <button style={pillDark} onClick={loadTournamentAchievements}>Refresh</button>
        </div>

        {tournamentAchievementsErr ? (
          <div style={{ marginTop: 12, color: "#ffb0b0", fontWeight: 800 }}>{tournamentAchievementsErr}</div>
        ) : tournamentAchievementsLoading ? (
          <div style={{ marginTop: 12, opacity: 0.8 }}>Loading tournament achievements…</div>
        ) : tournamentAchievements.length === 0 ? (
          <div style={{ marginTop: 12, opacity: 0.72 }}>
            No tournament championships yet. Tournament wins will appear here automatically after a real event is completed.
          </div>
        ) : (
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            {tournamentAchievements.map((achievement) => (
              <div
                key={achievement.id}
                style={{
                  minHeight: 260,
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 18,
                  border: "1px solid rgba(255,214,92,0.32)",
                  background: "linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.9)), url('/get-active-3v3-bracket.jpg') center / cover",
                  boxShadow: "0 16px 50px rgba(0,0,0,0.45)",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ width: "100%", padding: 18, background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.92) 35%)" }}>
                  <div style={{ color: "#ffd666", fontWeight: 1000, fontSize: 12, letterSpacing: 1.2 }}>TOURNAMENT CHAMPION</div>
                  <div style={{ marginTop: 6, color: "#fff", fontWeight: 1000, fontSize: 24 }}>{achievement.teamName}</div>
                  <div style={{ marginTop: 4, color: "rgba(255,255,255,0.8)", fontWeight: 800 }}>{achievement.tournamentName}</div>
                  <div style={{ marginTop: 8, color: "rgba(255,255,255,0.72)", fontSize: 13, lineHeight: 1.5 }}>
                    Final: {achievement.finalScoreText || `${achievement.finalScoreA}–${achievement.finalScoreB}`}
                    {achievement.runnerUpTeamName ? ` vs ${achievement.runnerUpTeamName}` : ""}
                    <br />Prize earned: <strong>${(Number(achievement.prizeEarnedCents || 0) / 100).toFixed(2)}</strong>
                    {achievement.completedAt ? <><br />{new Date(achievement.completedAt).toLocaleDateString()}</> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {String(user?.email || "").trim().toLowerCase() === "peteywhiskers@yahoo.com" ? (
        <div
          style={{
            marginTop: 18,
            borderRadius: 18,
            padding: 18,
            border: "1px solid rgba(255,74,88,0.38)",
            background:
              "radial-gradient(720px 300px at 10% 0%, rgba(220,38,38,0.18), transparent 65%), rgba(8,10,16,0.76)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#ff6975", fontSize: 12, fontWeight: 1000, letterSpacing: 1 }}>FOUNDER TOOLS</div>
              <div style={{ marginTop: 4, fontWeight: 1000, fontSize: 19 }}>✉️ Sponsor Inbox</div>
              <div style={{ marginTop: 5, opacity: 0.78, fontSize: 13, lineHeight: 1.5 }}>
                Send and receive partnership mail as team@gastreaming.live. This inbox is visible only to you.
              </div>
            </div>
            <button type="button" style={pillRed} onClick={() => nav("/admin/sponsors")}>Open Sponsor Inbox</button>
          </div>
        </div>
      ) : null}

      <PushNotificationSettings />

      <div
        id="notifications"
        style={{
          marginTop: 18,
          borderRadius: 18,
          padding: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          background:
            "radial-gradient(900px 460px at 15% 10%, rgba(0,255,170,0.10), transparent 60%), rgba(8, 10, 16, 0.70)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16 }}>Notifications</div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>
              Team invites, roster updates, comments, and other account activity show here.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={pillDark} onClick={loadNotifications}>
              Refresh
            </button>
            <button style={pillGreen} onClick={markAllNotificationsRead} disabled={notifActionBusy}>
              Mark all read
            </button>
            {notifications.some((notification) => Boolean(notification?.readAt || notification?.read)) ? (
              <button style={pillRed} onClick={clearReadNotifications} disabled={notifActionBusy}>
                Clear read
              </button>
            ) : null}
          </div>
        </div>

        {notifErr ? (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid rgba(255,0,64,0.22)",
              background: "rgba(255,0,64,0.10)",
              fontWeight: 900,
              fontSize: 13,
            }}
          >
            {notifErr}
          </div>
        ) : null}

        {notifLoading ? (
          <div style={{ marginTop: 12, opacity: 0.85 }}>Loading…</div>
        ) : notifications.length === 0 ? (
          <div style={{ marginTop: 12, opacity: 0.85 }}>No notifications yet.</div>
        ) : (
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {notifications.slice(0, 20).map((n) => {
              const isTournamentNotification = String(n.type || "").startsWith("tournament_");
              const fallbackTitle =
                n.type === "comment"
                  ? "💬 New comment"
                  : isTournamentNotification
                  ? "🏀 Tournament update"
                  : "🔔 Notification";
              const notificationTitle = n.title || fallbackTitle;
              const notificationBody = n.body || n.textPreview || "";

              return (
                <div
                  key={n.id}
                  style={{
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: n.read
                      ? "rgba(255,255,255,0.04)"
                      : isTournamentNotification
                      ? "linear-gradient(145deg, rgba(177,0,18,0.22), rgba(0,0,0,0.36))"
                      : "rgba(0,255,170,0.10)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>
                      {notificationTitle}
                      {!n.read ? <span style={{ marginLeft: 8, opacity: 0.75 }}>(unread)</span> : null}
                    </div>
                    {!n.read ? (
                      <button style={pillDark} onClick={() => markNotificationRead(n.id)} disabled={notifActionBusy}>
                        Mark read
                      </button>
                    ) : (
                      <button style={pillRed} onClick={() => deleteReadNotification(n.id)} disabled={notifActionBusy}>
                        Remove
                      </button>
                    )}
                  </div>

                  {n.fromUsername ? (
                    <div style={{ marginTop: 6, opacity: 0.72, fontSize: 13 }}>
                      From: {n.fromUsername === "GAStreaming" ? "GAStreaming" : `@${n.fromUsername}`}
                    </div>
                  ) : null}

                  {notificationBody ? (
                    <div style={{ marginTop: 7, fontSize: 14, lineHeight: 1.55 }}>{notificationBody}</div>
                  ) : null}

                  {n.href ? (
                    <button
                      style={{
                        ...pillGreen,
                        marginTop: 10,
                      }}
                      onClick={async () => {
                        if (!n.read) await markNotificationRead(n.id);
                        nav(n.href);
                      }}
                    >
                      {isTournamentNotification ? "View Tournament" : "Open"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.10)", margin: "22px 0" }} />

      <PublicProfileCustomizer user={user} />

      <div style={{ height: 1, background: "rgba(255,255,255,0.10)", margin: "22px 0" }} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 0.9fr",
          gap: 24,
        }}
      >
        <div>
          <div style={sectionTitle}>About</div>
          <p style={sectionText}>
            {bio
              ? bio
              : "Share who you are, what you play, and what the Get Active squad should know about you."}
          </p>
        </div>

        <div>
          <div style={sectionTitle}>Social links</div>
          <div style={linkList}>
            {Object.values(socials || {}).filter(Boolean).length === 0 ? (
              <div style={{ ...sectionText, opacity: 0.75 }}>
                No links added yet. Hit <strong>Edit profile</strong> to add your Twitch, YouTube,
                TikTok, and more.
              </div>
            ) : (
              <>
                {socials.twitch ? (
                  <div style={linkRow}>
                    <div style={{ fontWeight: 800 }}>Twitch</div>
                    <a
                      href={socials.twitch}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "rgba(255,140,170,0.95)" }}
                    >
                      Open
                    </a>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div style={{ height: 14 }} />

          <div style={sectionTitle}>Uploads</div>

          {uploadsLoading ? (
            <div style={{ ...sectionText, opacity: 0.75 }}>Loading uploads…</div>
          ) : uploadsErr ? (
            <div style={{ ...sectionText, opacity: 0.75 }}>Uploads error: {uploadsErr}</div>
          ) : uploads.length === 0 ? (
            <div style={{ ...sectionText, opacity: 0.8 }}>
              No gameplay posts yet. Click <strong>Upload gameplay</strong> to post your first clip.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {uploads.slice(0, 10).map((p) => {
                const isClip = Boolean(p?.isClip || p?.clip);
                const clipWaiting = Boolean(
                  isClip && p?.clipPending && !p?.clipFinalized
                );

                return (
                  <div key={p.id} style={uploadCard}>
                    <div style={{ fontWeight: 900 }}>
                      {isClip ? "🎬 " : ""}
                      {p.caption ? p.caption : isClip ? "Stream clip" : "Gameplay post"}
                    </div>

                    {clipWaiting ? (
                      <div
                        style={{
                          ...videoWrap,
                          height: 220,
                          display: "grid",
                          placeItems: "center",
                          textAlign: "center",
                          padding: 18,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 900, marginBottom: 6 }}>
                            {p?.clipNeedsReclip ? "Clip needs to be recreated" : "Clip is processing"}
                          </div>
                          <div style={{ fontSize: 13, opacity: 0.78 }}>
                            {p?.clipNeedsReclip
                              ? "This older clip was linked to a broken live trim. Delete it and run !clip again while live."
                              : "This clip will play after the live stream ends and Mux finishes the recording."}
                          </div>
                        </div>
                      </div>
                    ) : p.playbackId ? (
                      <div style={videoWrap}>
                        {isClip ? (
                          <ProfileClipPlayer post={p} />
                        ) : (
                          <MuxPlayer
                            playbackId={p.playbackId}
                            streamType={p?.streamType === "live" ? "live" : "on-demand"}
                            autoPlay={false}
                            muted={false}
                            controls
                            style={{ width: "100%", height: 220, display: "block" }}
                          />
                        )}
                      </div>
                    ) : null}

                    {isClip ? (
                      <div style={{ fontSize: 13, opacity: 0.85 }}>
                        30-second stream clip
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, opacity: 0.85 }}>
                        Playback ID: <span style={{ opacity: 0.95 }}>{p.playbackId}</span>
                        {Number(p?.durationSeconds || 0) > 0
                          ? ` • ${formatDuration(p.durationSeconds)}`
                          : ""}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {!isClip && p.playbackId ? (
                        <a
                          href={`https://stream.mux.com/${p.playbackId}.m3u8`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ ...pillDark, textDecoration: "none", display: "inline-block" }}
                        >
                          Open video stream
                        </a>
                      ) : null}

                      <button
                        style={pillRed}
                        onClick={() => deletePost(p.id)}
                        disabled={deletingId === String(p.id)}
                        title="Deletes from your profile and the Feed"
                      >
                        {deletingId === String(p.id) ? "Deleting..." : "Delete"}
                      </button>
                    </div>

                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleString() : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <button style={pillDark} onClick={loadUploads}>
              Refresh uploads
            </button>
          </div>
        </div>
      </div>

      <div
        id="account-settings"
        style={{
          marginTop: 22,
          borderRadius: 18,
          padding: 16,
          border: "1px solid rgba(255,70,70,0.28)",
          background:
            "radial-gradient(900px 460px at 15% 10%, rgba(255,0,40,0.12), transparent 60%), rgba(8, 10, 16, 0.74)",
        }}
      >
        <div style={{ fontWeight: 1000, fontSize: 18 }}>Account &amp; data</div>
        <div style={{ marginTop: 6, opacity: 0.78, fontSize: 13, lineHeight: 1.55 }}>
          Permanently delete your GAStreaming account and associated personal data. For security,
          deletion requires your current password and a final confirmation.
        </div>
        <button
          type="button"
          style={{
            ...pillRed,
            marginTop: 14,
            minWidth: 190,
          }}
          onClick={() => nav("/delete-account")}
        >
          Delete my account
        </button>
      </div>

      {/* Upload modal */}
      {badgesOpen ? (
        <div role="dialog" aria-modal="true" onClick={() => setBadgesOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10050, background: "rgba(0,0,0,.78)", display: "grid", placeItems: "center", padding: 18 }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: "min(760px, 96vw)", maxHeight: "86vh", overflowY: "auto", borderRadius: 22, padding: 20, border: "1px solid rgba(255,255,255,.16)", background: "linear-gradient(145deg,#26040e,#08090d)", boxShadow: "0 24px 80px rgba(0,0,0,.75)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 14 }}>
              <div><div style={{ fontSize: 26, fontWeight: 1000 }}>My Badges</div><div style={{ opacity: .72, marginTop: 4 }}>Unlock achievements and choose one identity badge for your profile, navigation name, live chat, and Feed comments.</div></div>
              <button style={pillDark} onClick={() => setBadgesOpen(false)}>✕</button>
            </div>
            {badgeErr ? <div style={{ padding: 10, color: "#ffb2bd" }}>{badgeErr}</div> : null}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
              {badgeCatalog.map((badge) => {
                const isCustomBadge = badge.key === "custom_badge";
                const canEquip = !isCustomBadge || badge.customImageReady;
                return (
                  <div key={badge.key} style={{ border: `1px solid ${badge.unlocked ? "rgba(255,210,70,.45)" : "rgba(255,255,255,.1)"}`, borderRadius: 18, padding: 14, background: badge.unlocked ? "rgba(110,55,0,.18)" : "rgba(0,0,0,.28)", opacity: badge.unlocked ? 1 : .58 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <img src={getBadgeImage(badge)} alt="" style={{ width: 58, height: 58, borderRadius: "50%", objectFit: "cover", filter: badge.unlocked ? "none" : "grayscale(1)" }} />
                      <div>
                        <div style={{ fontWeight: 1000 }}>{badge.unlocked ? "🏅" : "🔒"} {badge.title}</div>
                        <div style={{ fontSize: 12, opacity: .78, marginTop: 4 }}>{badge.description}</div>
                      </div>
                    </div>
                    {badge.unlocked && isCustomBadge ? (
                      <button
                        type="button"
                        style={{ ...pillDark, width: "100%", marginTop: 12 }}
                        disabled={customBadgeBusy}
                        onClick={() => customBadgeFileRef.current?.click()}
                      >
                        {customBadgeBusy ? "Saving custom badge…" : badge.customImageReady ? "Replace custom badge image" : "Upload custom badge image"}
                      </button>
                    ) : null}
                    {badge.unlocked ? (
                      <button
                        type="button"
                        style={{ ...pillGreen, width: "100%", marginTop: 10 }}
                        disabled={!!badgeBusy || badge.equipped || !canEquip}
                        onClick={() => equipBadge(badge.key)}
                      >
                        {badge.equipped ? "Equipped" : !canEquip ? "Upload image to equip" : badgeBusy === badge.key ? "Equipping…" : "Equip badge"}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {equippedBadge ? <button type="button" style={{ ...pillDark, width: "100%", marginTop: 14 }} disabled={!!badgeBusy} onClick={() => equipBadge("")}>Remove equipped badge</button> : null}
          </div>
        </div>
      ) : null}

      {avatarCropOpen ? (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeAvatarCrop();
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10030,
            background: "rgba(0,0,0,0.78)",
            display: "grid",
            placeItems: "center",
            padding: 18,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Position profile photo"
            style={{
              width: "min(520px, 96vw)",
              maxHeight: "92vh",
              overflowY: "auto",
              borderRadius: 22,
              border: "1px solid rgba(255,255,255,0.16)",
              background:
                "radial-gradient(700px 360px at 50% -10%, rgba(255,0,64,0.24), transparent 65%), rgba(10,8,13,0.98)",
              boxShadow: "0 28px 90px rgba(0,0,0,0.72)",
              padding: 20,
              color: "rgba(255,255,255,0.95)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "start" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 24 }}>Position profile photo</h2>
                <div style={{ marginTop: 6, opacity: 0.78, fontSize: 13, lineHeight: 1.4 }}>
                  Drag the picture until it looks right inside the circle. Use the slider to zoom.
                </div>
              </div>
              <button
                type="button"
                onClick={() => closeAvatarCrop()}
                disabled={avatarCropSaving}
                aria-label="Close photo positioner"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.07)",
                  color: "white",
                  fontSize: 22,
                  cursor: avatarCropSaving ? "not-allowed" : "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", placeItems: "center", marginTop: 20 }}>
              <div
                onPointerDown={startAvatarCropDrag}
                onPointerMove={moveAvatarCropDrag}
                onPointerUp={finishAvatarCropDrag}
                onPointerCancel={finishAvatarCropDrag}
                onDoubleClick={resetAvatarCrop}
                title="Drag to reposition. Double-click to reset."
                style={{
                  position: "relative",
                  width: AVATAR_CROP_PREVIEW_SIZE,
                  height: AVATAR_CROP_PREVIEW_SIZE,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "4px solid rgba(255,255,255,0.92)",
                  outline: "6px solid rgba(255,0,64,0.32)",
                  background: "rgba(0,0,0,0.55)",
                  cursor: avatarCropDragging ? "grabbing" : "grab",
                  touchAction: "none",
                  userSelect: "none",
                  boxShadow: "0 14px 42px rgba(0,0,0,0.58)",
                }}
              >
                <img
                  src={avatarCropSrc}
                  alt="Profile crop preview"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: avatarCropGeometry.displayWidth,
                    height: avatarCropGeometry.displayHeight,
                    maxWidth: "none",
                    transform: `translate(-50%, -50%) translate(${avatarCropOffset.x}px, ${avatarCropOffset.y}px)`,
                    transformOrigin: "center",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: "50% auto auto 50%",
                    width: 18,
                    height: 18,
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.70)",
                    boxShadow: "0 0 0 999px rgba(0,0,0,0.04)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            <label style={{ display: "grid", gap: 8, marginTop: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 13 }}>
                <span>Zoom</span>
                <span>{Math.round(avatarCropZoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={avatarCropZoom}
                disabled={avatarCropSaving}
                onChange={(event) => setAvatarCropZoom(Number(event.target.value || 1))}
                style={{ width: "100%", accentColor: "#ff174d" }}
              />
            </label>

            {avatarCropErr ? (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,70,90,0.35)",
                  background: "rgba(255,0,50,0.12)",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {avatarCropErr}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "space-between",
                flexWrap: "wrap",
                marginTop: 18,
              }}
            >
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={pillDark}
                  disabled={avatarCropSaving}
                  onClick={() => avatarFileRef.current?.click()}
                >
                  Choose another photo
                </button>
                <button type="button" style={pillDark} disabled={avatarCropSaving} onClick={resetAvatarCrop}>
                  Reset position
                </button>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" style={pillDark} disabled={avatarCropSaving} onClick={() => closeAvatarCrop()}>
                  Cancel
                </button>
                <button type="button" style={pillGreen} disabled={avatarCropSaving} onClick={savePositionedAvatar}>
                  {avatarCropSaving ? "Saving photo…" : "Save photo"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {uploadOpen ? createPortal(
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "grid",
            placeItems: "center",
            padding: 18,
            zIndex: 11000,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Upload gameplay"
            style={{
              width: "min(720px, 100%)",
              maxHeight: "calc(100dvh - 36px)",
              overflowY: "auto",
              borderRadius: 18,
              padding: 18,
              background: "rgba(10,10,10,0.82)",
              border: "1px solid rgba(255,255,255,0.14)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
              backdropFilter: "blur(10px)",
              color: "rgba(255,255,255,0.92)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 900 }}>Upload gameplay</div>
              <button
                type="button"
                aria-label="Close upload window"
                title={uploading ? "Finish or wait for the upload before closing" : "Close"}
                style={{ ...pillDark, width: 42, height: 42, padding: 0, fontSize: 20 }}
                onClick={() => setUploadOpen(false)}
                disabled={uploading}
              >
                ✕
              </button>
            </div>

            {uploadErr ? (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,80,80,0.35)",
                  background: "rgba(120,0,0,0.25)",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {uploadErr}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.9 }}>Caption (optional)</div>
                <input
                  value={uploadCaption}
                  onChange={(e) => setUploadCaption(e.target.value)}
                  placeholder="Add a caption for your gameplay post…"
                  disabled={uploading}
                  style={{
                    padding: "12px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(0,0,0,0.35)",
                    color: "rgba(255,255,255,0.92)",
                    outline: "none",
                  }}
                />
              </label>

              {uploading ? (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(0,0,0,0.25)",
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 13 }}>{uploadStep || "Working…"}</div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>Progress: {uploadProgress}%</div>
                  <div
                    style={{
                      height: 10,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.10)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${uploadProgress}%`,
                        background: "linear-gradient(90deg, rgba(255,0,64,0.90), rgba(190,0,38,0.78))",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button style={pillDark} onClick={pickUploadFile}>
                    Choose video &amp; upload
                  </button>
                </div>
              )}

              <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.5 }}>
                Gameplay uploads must be <strong>2:30 or shorter</strong>. Uploads now use smaller resumable chunks with automatic retries. You can briefly switch apps, but do not force-close GAStreaming until the upload reaches 100%.
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Edit modal */}
      {editOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setEditOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "grid",
            placeItems: "center",
            padding: 18,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "min(720px, 100%)",
              borderRadius: 18,
              padding: 18,
              background: "rgba(10,10,10,0.82)",
              border: "1px solid rgba(255,255,255,0.14)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
              backdropFilter: "blur(10px)",
              color: "rgba(255,255,255,0.92)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 900 }}>Edit profile</div>
              <button style={pillDark} onClick={() => setEditOpen(false)} disabled={saving}>
                Close
              </button>
            </div>

            {editErr ? (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,80,80,0.35)",
                  background: "rgba(120,0,0,0.25)",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {editErr}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.9 }}>Username</div>
                <input
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  placeholder="Your unique GAStreaming username"
                  maxLength={24}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{
                    padding: "12px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(0,0,0,0.35)",
                    color: "rgba(255,255,255,0.92)",
                    outline: "none",
                  }}
                />
                <div style={{ fontSize: 12, opacity: 0.72, lineHeight: 1.45 }}>
                  3-24 characters: letters, numbers, periods, underscores, or hyphens.
                  Usernames are unique regardless of capitalization. Once changed, your old
                  username becomes available to another member.
                </div>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.9 }}>Display name</div>
                <input
                  value={formDisplayName}
                  onChange={(e) => setFormDisplayName(e.target.value)}
                  placeholder="Your name on Get Active"
                  style={{
                    padding: "12px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(0,0,0,0.35)",
                    color: "rgba(255,255,255,0.92)",
                    outline: "none",
                  }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.9 }}>Bio</div>
                <textarea
                  value={formBio}
                  onChange={(e) => setFormBio(e.target.value)}
                  placeholder="What do you stream? What should people know?"
                  rows={4}
                  style={{
                    padding: "12px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(0,0,0,0.35)",
                    color: "rgba(255,255,255,0.92)",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
              </label>


              <div
                style={{
                  marginTop: 6,
                  paddingTop: 10,
                  borderTop: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div style={{ fontWeight: 900, fontSize: 13, opacity: 0.92, marginBottom: 10 }}>
                  Social links
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  }}
                >
                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>Twitch</div>
                    <input
                      value={socialTwitch}
                      onChange={(e) => setSocialTwitch(e.target.value)}
                      placeholder="https://twitch.tv/yourname"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>YouTube</div>
                    <input
                      value={socialYouTube}
                      onChange={(e) => setSocialYouTube(e.target.value)}
                      placeholder="https://youtube.com/@yourname"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>TikTok</div>
                    <input
                      value={socialTikTok}
                      onChange={(e) => setSocialTikTok(e.target.value)}
                      placeholder="https://tiktok.com/@yourname"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>Instagram</div>
                    <input
                      value={socialInstagram}
                      onChange={(e) => setSocialInstagram(e.target.value)}
                      placeholder="https://instagram.com/yourname"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>X / Twitter</div>
                    <input
                      value={socialTwitter}
                      onChange={(e) => setSocialTwitter(e.target.value)}
                      placeholder="https://x.com/yourname"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 12, opacity: 0.85 }}>Website</div>
                    <input
                      value={socialWebsite}
                      onChange={(e) => setSocialWebsite(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      style={{
                        padding: "12px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.14)",
                        background: "rgba(0,0,0,0.35)",
                        color: "rgba(255,255,255,0.92)",
                        outline: "none",
                      }}
                    />
                  </label>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75, lineHeight: 1.35 }}>
                  Tip: paste full links (including https://). You can leave any blank.
                </div>
              </div>


              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                  marginTop: 6,
                  flexWrap: "wrap",
                }}
              >
                <button style={pillDark} onClick={onPickAvatar} disabled={saving}>
                  Position / change profile pic
                </button>
                <button style={pillGreen} onClick={saveProfile} disabled={saving}>
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        @media (max-width: 900px) {
          ._ga_profile_grid_fix { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 540px) {
          ._ga_profile_identity_cluster { gap: 14px !important; }
          ._ga_profile_identity_cluster > div:first-child { width: 96px !important; height: 96px !important; flex: 0 0 96px !important; }
        }
      `}</style>
    </div>
  );
}

function ProfileClipPlayer({ post }) {
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const start = Math.max(0, Number(post?.clipStartOffsetSeconds || 0));
  const rawEnd = Number(post?.clipEndOffsetSeconds || 0);
  const configuredDuration = Math.max(1, Number(post?.durationSeconds || 30));
  const availableDuration = rawEnd > start ? rawEnd - start : configuredDuration;
  const duration = Math.max(1, Math.round(Math.min(availableDuration, configuredDuration)));
  const end = start + duration;

  function getPlayer() {
    return playerRef.current;
  }

  function syncToClipStart(forceStart = false) {
    const player = getPlayer();
    if (!player) return;

    const current = Number(player.currentTime || 0);
    if (forceStart || current < start || current >= end) {
      try {
        player.currentTime = start;
      } catch {}
      setElapsed(0);
      return;
    }

    setElapsed(Math.max(0, Math.min(duration, current - start)));
  }

  function togglePlay() {
    const player = getPlayer();
    if (!player) return;

    syncToClipStart(Number(player.currentTime || 0) >= end);

    if (player.paused) {
      Promise.resolve(player.play()).catch(() => {});
    } else {
      player.pause();
    }
  }

  function replayClip() {
    const player = getPlayer();
    if (!player) return;
    try {
      player.currentTime = start;
    } catch {}
    setElapsed(0);
    Promise.resolve(player.play()).catch(() => {});
  }

  useEffect(() => {
    const player = getPlayer();
    if (!player) return undefined;

    const onLoaded = () => syncToClipStart(true);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    const onTime = () => {
      const current = Number(player.currentTime || 0);

      if (current < start) {
        try {
          player.currentTime = start;
        } catch {}
        setElapsed(0);
        return;
      }

      if (current >= end) {
        try {
          player.pause();
          player.currentTime = start;
        } catch {}
        setPlaying(false);
        setElapsed(duration);
        return;
      }

      setElapsed(Math.max(0, Math.min(duration, current - start)));
    };

    player.addEventListener("loadedmetadata", onLoaded);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    player.addEventListener("ended", onEnded);
    player.addEventListener("timeupdate", onTime);

    return () => {
      player.removeEventListener("loadedmetadata", onLoaded);
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
      player.removeEventListener("ended", onEnded);
      player.removeEventListener("timeupdate", onTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, start, end, duration]);

  const progress = Math.max(0, Math.min(100, (elapsed / duration) * 100));

  return (
    <div
      className="ga-profile-clip-player"
      style={{
        position: "relative",
        height: 220,
        background: "black",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      <style>{`
        .ga-profile-clip-player mux-player {
          --controls: none;
          --media-control-display: none;
          --media-controls-display: none;
          --media-control-bar-display: none;
          --play-button-display: none;
          --seek-backward-button-display: none;
          --seek-forward-button-display: none;
          --mute-button-display: none;
          --captions-button-display: none;
          --airplay-button-display: none;
          --pip-button-display: none;
          --fullscreen-button-display: none;
          --time-display-display: none;
          --duration-display-display: none;
          --rendition-selectmenu-display: none;
          --playback-rate-button-display: none;
          --seek-live-button-display: none;
          --live-button-display: none;
        }
      `}</style>

      <MuxPlayer
        ref={playerRef}
        streamType="on-demand"
        playbackId={String(post?.playbackId || "").trim()}
        startTime={start}
        controls={false}
        nohotkeys
        metadata={{
          video_id: String(post?.id || ""),
          video_title: post?.caption || "Stream clip",
          viewer_user_id: String(post?.userId || "member"),
        }}
        style={{
          width: "100%",
          height: 220,
          display: "block",
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? "Pause clip" : "Play clip"}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 4,
          width: "100%",
          height: "100%",
          border: 0,
          background: "transparent",
          color: "white",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontWeight: 900,
          fontSize: 42,
          textShadow: "0 4px 18px rgba(0,0,0,0.75)",
        }}
      >
        {playing ? "" : "▶"}
      </button>

      <div
        style={{
          position: "absolute",
          left: 10,
          right: 10,
          bottom: 10,
          zIndex: 6,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 9px",
          borderRadius: 999,
          background: "rgba(0,0,0,0.88)",
          border: "1px solid rgba(255,255,255,0.16)",
          pointerEvents: "auto",
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(255,255,255,0.10)",
            color: "white",
            cursor: "pointer",
            fontWeight: 900,
          }}
        >
          {playing ? "❚❚" : "▶"}
        </button>

        <div
          style={{
            flex: 1,
            height: 7,
            borderRadius: 999,
            overflow: "hidden",
            background: "rgba(255,255,255,0.18)",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #22d3ee, #f472b6)",
            }}
          />
        </div>

        <div style={{ fontSize: 12, fontWeight: 900, color: "white", minWidth: 56 }}>
          {Math.floor(elapsed)} / {duration}s
        </div>

        <button
          type="button"
          onClick={replayClip}
          aria-label="Replay clip"
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(255,255,255,0.10)",
            color: "white",
            cursor: "pointer",
            fontWeight: 900,
          }}
        >
          ↺
        </button>
      </div>
    </div>
  );
}

